// F1 App-Shell (TES-948): Rollen-gruppierte, gefilterte Nav-Sektionen.
// Eine Quelle der Wahrheit fuer die Section-Komposition, die SideNav UND
// NavDrawer identisch brauchen (operativ/QM/admin/super/platform) — verhindert
// Duplikation + senkt die Komplexitaet beider Komponenten. Logik 1:1 aus
// AppLayout (GuestMode/Offline/Produktionsmodus-Filter erhalten).
import type { ShellModel } from "./useShellModel";
import {
  navigation,
  qmNavigation,
  qmAdminNavigation,
  tenantAdminNavigation,
  adminNavigation,
  superAdminNavigation,
  platformToolsNavigation,
  type NavItem,
} from "./navConfig";

export interface NavSection {
  /** i18n-Key fuer das Section-Label, oder null fuer die label-lose Haupt-Nav. */
  labelKey: string | null;
  items: NavItem[];
  /** true = operative Haupt-Nav (Badges + per-item Offline-Guard). */
  isOperative: boolean;
  /** true = Section-Label dimmt bei Offline (operativ/QM/admin). */
  dimWhenOffline: boolean;
}

// TES-2061 / TES-2008: EINE Quelle für den React-Key je Nav-Sektion — SideNav UND NavDrawer
// rendern die `useNavSections`-Liste und MÜSSEN identisch keyen, sonst driften sie erneut
// auseinander. Mehrere label-lose Sektionen (z.B. operative + qm_admin) dürfen NICHT auf einen
// festen Key ("__operative") kollabieren → Geschwister-Fragmente hätten kollidierende Keys
// (Duplikat-Rendering). Der Index-Fallback garantiert Eindeutigkeit; contract-getestet in
// navConfig.contract.test.ts.
export function navSectionKey(section: NavSection, idx: number): string {
  return section.labelKey ?? `sec-${idx}`;
}

// Sichtbarkeit der jeweiligen Section — als reine Praedikate aus dem Hook-Body gezogen,
// damit useNavSections selbst nur noch geradlinig komponiert (Komplexitaet sinkt, Logik 1:1).
interface NavContext {
  roles: string[];
  isAdmin: boolean;
  isQualitaetsmanager: boolean;
  isQmAdmin: boolean;
  isSuperAdmin: boolean;
  isTenantContext: boolean;
  isGuestMode: boolean;
  isInProductionMode: boolean | null;
}

function itemHasRole(item: NavItem, roles: string[]): boolean {
  if (!item.roles || item.roles.length === 0) return true;
  return item.roles.some((r) => roles.includes(r));
}

// TES-1092: super_admin (global, ohne Tenant-Kontext) sieht die volle Navigation —
// operative + QM + Admin-Gruppe ZUSÄTZLICH zu Super-Admin/Plattform. Nur der
// Guest-Mode (Tenant-Kontext) blendet operative/QM-Bereiche weiterhin aus.
function showsAdmin(ctx: NavContext): boolean {
  return ctx.isAdmin || ctx.isSuperAdmin;
}

// dev-tools nur im Nicht-Produktionsmodus; setup-status nicht im GuestMode (1:1 aus AppLayout).
function isAdminItemHidden(href: string, ctx: NavContext): boolean {
  const devToolsInProd = href === "/admin/dev-tools" && ctx.isInProductionMode === true;
  const setupInGuest = href === "/admin/setup-status" && ctx.isGuestMode;
  return devToolsInProd || setupInGuest;
}

function buildOperativeSection(ctx: NavContext): NavSection | null {
  if (ctx.isTenantContext) return null;
  const items = navigation.filter((i) => itemHasRole(i, ctx.roles));
  // qm_admin steht in keiner navigation-roles-Liste → seine operative Sektion wäre leer; er bekommt
  // seine kuratierte Sicht über buildQmAdminSection (TES-1973). Für ALLE anderen Rollen bleibt die
  // (ggf. leere) operative Sektion erhalten (roles=[]-Fail-safe, TES-1092).
  if (items.length === 0 && ctx.isQmAdmin) return null;
  return { labelKey: null, items, isOperative: true, dimWhenOffline: false };
}

// qm_admin (Org-Hülle, TES-1973): eigene kuratierte Sicht — Arbeitsflächen + Oversight-Lesesichten.
// Primär-Nav (labelKey null, mit Badges) statt eigener Gruppen-Header, da dies die Haupt-
// Arbeitsoberfläche der Rolle ist. qm_admin steht in KEINER operativen navigation-roles-Liste, also
// ist buildOperativeSection für ihn null → hier kein Doppel-Rendern.
function buildQmAdminSection(ctx: NavContext): NavSection | null {
  if (ctx.isTenantContext || !ctx.isQmAdmin) return null;
  return { labelKey: null, items: qmAdminNavigation, isOperative: true, dimWhenOffline: false };
}

// super_admin sieht '/admin/roles' bereits in der Admin-Gruppe — Duplikat vermeiden.
function qmItemsFor(ctx: NavContext): NavItem[] {
  if (!ctx.isSuperAdmin) return qmNavigation;
  return qmNavigation.filter((i) => !adminNavigation.some((a) => a.href === i.href));
}

function buildQmSection(ctx: NavContext): NavSection | null {
  const seesQm = ctx.isQualitaetsmanager || ctx.isSuperAdmin;
  if (ctx.isTenantContext || !seesQm) return null;
  return { labelKey: "nav.qm", items: qmItemsFor(ctx), isOperative: false, dimWhenOffline: true };
}

function buildAdminSection(ctx: NavContext): NavSection | null {
  if (!showsAdmin(ctx)) return null;
  const items = [...tenantAdminNavigation, ...adminNavigation].filter(
    (i) => !isAdminItemHidden(i.href, ctx),
  );
  return { labelKey: "nav.administration", items, isOperative: false, dimWhenOffline: true };
}

function buildSuperAdminSections(ctx: NavContext): NavSection[] {
  if (!ctx.isSuperAdmin || ctx.isTenantContext) return [];
  const platformItems = platformToolsNavigation.filter((i) => !isAdminItemHidden(i.href, ctx));
  return [
    {
      labelKey: "nav.superAdmin",
      items: superAdminNavigation,
      isOperative: false,
      dimWhenOffline: false,
    },
    { labelKey: "nav.platform", items: platformItems, isOperative: false, dimWhenOffline: false },
  ];
}

export function useNavSections(model: ShellModel): NavSection[] {
  const {
    roles,
    isAdmin,
    isQualitaetsmanager,
    isSuperAdmin,
    isTenantContext,
    isGuestMode,
    isInProductionMode,
  } = model;
  const ctx: NavContext = {
    roles,
    isAdmin,
    isQualitaetsmanager,
    isQmAdmin: roles.includes("qm_admin"),
    isSuperAdmin,
    isTenantContext,
    isGuestMode,
    isInProductionMode,
  };

  return [
    buildOperativeSection(ctx),
    buildQmAdminSection(ctx),
    buildQmSection(ctx),
    buildAdminSection(ctx),
    ...buildSuperAdminSections(ctx),
  ].filter((s): s is NavSection => s !== null);
}
